import * as signalR from '@microsoft/signalr';
import { apiRequest } from '../authConfig';

// UDRAW-S5 §2.1 — thin SignalR wrapper for the live layer. One
// HubConnection per browser tab (shared across the whole My Projects
// experience). Callers use:
//
//   await connect();                       // idempotent, safe re-entrant
//   await joinProject(projectId);          // enter the project group
//   await leaveProject(projectId);         // exit + drop presence
//   on(event, handler);                    // subscribe (returns unsubscribe fn)
//   sendFocus(projectId, focus | null);    // presence update on drawer open/close
//   startEditing({ projectId, taskId?, workItemId?, fieldKey });
//   stopEditing ({ projectId, taskId?, workItemId?, fieldKey });
//   disconnect();                          // hard tear down (leaving the app)
//
// Events surfaced from the hub (spec §1.2):
//   deliveryChanged   { projectId, kind, taskId?, workItemId? }
//   presenceChanged   { projectId, viewers: [{ employeeId, fullName, focus }] }
//   fieldEditing      { projectId, taskId?, workItemId?, fieldKey,
//                       employeeId, fullName, active }
//
// Discipline (spec §2.3):
//   • Only signals — no data mutations flow through here.
//   • A failed connection degrades silently — the app keeps working
//     via the REST endpoints exactly like before S5.
//   • Automatic reconnect + a fresh Join of every previously-joined
//     project on reconnect (so a network blip doesn't strand the
//     client outside its groups).

const API_BASE_URL = (typeof window !== 'undefined' && window.__API_BASE_URL__) || '/api';

// Strip the trailing /api so the hub URL sits at /hubs/projects.
const HUB_URL = API_BASE_URL.replace(/\/api\/?$/i, '') + '/hubs/projects';

// Module-level state so subscribers survive component remounts (page
// nav between /my-projects and /my-projects/:id keeps the connection).
let connection = null;
let starting = null;
// Track which project groups THIS client has joined so we can
// re-Join them on reconnect. Also drives sensible dedupe on repeated
// joinProject calls.
const joinedProjects = new Set();
// event → Set<handler>. Local dispatch that fans one hub event out to
// every subscriber; simpler than juggling connection.on stacks per
// consumer, and makes unsubscribe cheap.
const subscribers = {
  deliveryChanged: new Set(),
  presenceChanged: new Set(),
  fieldEditing: new Set(),
};

function dispatch(event, payload) {
  const set = subscribers[event];
  if (!set) return;
  set.forEach((fn) => {
    try { fn(payload); } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[live] subscriber threw', event, err);
    }
  });
}

async function resolveAccessToken() {
  // Same MSAL surface axios uses (see api.js). We ask silently for the
  // API scope; if the acquire fails we return null and let the hub 401.
  try {
    const msal = typeof window !== 'undefined' ? window.__msalInstance : null;
    if (!msal || !Array.isArray(apiRequest.scopes) || apiRequest.scopes.length === 0) return null;
    const accounts = msal.getAllAccounts?.() || [];
    if (accounts.length === 0) return null;
    const request = { ...apiRequest, account: accounts[0] };
    const result = await msal.acquireTokenSilent(request);
    return result?.accessToken || null;
  } catch {
    return null;
  }
}

function buildConnection() {
  return new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: () => resolveAccessToken(),
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();
}

function wireHandlers(conn) {
  conn.on('deliveryChanged', (p) => dispatch('deliveryChanged', p));
  conn.on('presenceChanged', (p) => dispatch('presenceChanged', p));
  conn.on('fieldEditing', (p) => dispatch('fieldEditing', p));

  conn.onreconnected(async () => {
    // Re-Join everything we were in when the connection dropped, then
    // let subscribers refetch — DeliveryPage listens for
    // deliveryChanged with kind === 'reconnect' as a signal to
    // full-reload the view.
    for (const pid of Array.from(joinedProjects)) {
      try { await conn.invoke('JoinProject', pid); } catch { /* swallow */ }
    }
    dispatch('deliveryChanged', { projectId: null, kind: 'reconnect' });
  });
}

export async function connect() {
  if (connection && connection.state === signalR.HubConnectionState.Connected) return connection;
  if (starting) return starting;
  connection = buildConnection();
  wireHandlers(connection);
  starting = connection.start()
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[live] connect failed — degrading to non-live', err?.message || err);
      connection = null;
      return null;
    })
    .finally(() => { starting = null; });
  return starting;
}

export async function joinProject(projectId) {
  if (!Number.isFinite(projectId) || projectId <= 0) return;
  await connect();
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try {
    await connection.invoke('JoinProject', projectId);
    joinedProjects.add(projectId);
  } catch (err) {
    // Non-fatal — the app keeps working via REST.
    // eslint-disable-next-line no-console
    console.warn('[live] JoinProject failed', projectId, err?.message || err);
  }
}

export async function leaveProject(projectId) {
  joinedProjects.delete(projectId);
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try { await connection.invoke('LeaveProject', projectId); } catch { /* swallow */ }
}

export function on(event, handler) {
  const set = subscribers[event];
  if (!set || typeof handler !== 'function') return () => {};
  set.add(handler);
  return () => set.delete(handler);
}

export async function sendFocus(projectId, focus) {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try { await connection.invoke('SetFocus', projectId, focus || null); } catch { /* swallow */ }
}

export async function startEditing(payload) {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try { await connection.invoke('StartEditing', payload); } catch { /* swallow */ }
}

export async function stopEditing(payload) {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  try { await connection.invoke('StopEditing', payload); } catch { /* swallow */ }
}

export async function disconnect() {
  joinedProjects.clear();
  const c = connection;
  connection = null;
  starting = null;
  if (c) {
    try { await c.stop(); } catch { /* swallow */ }
  }
}

// Test hook — clear all subscribers and the joined-projects set
// between test runs. Only exposed for Jest.
export function __resetForTests() {
  Object.values(subscribers).forEach((s) => s.clear());
  joinedProjects.clear();
  connection = null;
  starting = null;
}
